import {
  Component,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TaskModel, SubtaskModel, TaskMgmtService } from '../../service/task-mgmt.service';
import moment from 'moment';
import { FieldOption } from '../../service/workflow.service';
import {
  SubtaskDropConfirmPayload,
  SubtaskMovePayload,
} from '../subtask-mini-kanban/subtask-mini-kanban.component';
import { SessionService } from 'src/app/services/session.service';
import { DialogService } from 'src/app/services/dialog.service';

@Component({
    selector: 'app-subtask-drilldown-panel',
    templateUrl: './subtask-drilldown-panel.component.html',
    styleUrls: ['./subtask-drilldown-panel.component.scss'],
    standalone: false
})
export class SubtaskDrilldownPanelComponent implements OnDestroy {
  static minArrayLength(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const len = Array.isArray(control.value) ? control.value.length : 0;
      return len >= min ? null : { minArrayLength: { requiredLength: min, actualLength: len } };
    };
  }

  /** Scroll target for auto-scroll-to-bottom on the Updates tab. This is the
   *  `.st-modal-body` wrapper that contains the scrollable content. */
  @ViewChild('stModalBody') stModalBody?: ElementRef;

  // ---- Sidebar Inputs ----
  @Input() isOpen: boolean = false;
  @Input() task: TaskModel | null = null;
  /** Subtasks loaded by the parent (TaskManagementWfComponent) via the API. */
  @Input() subtasks: SubtaskModel[] = [];
  @Input() statusOptions: FieldOption[] = [];
  @Input() priorityOptions: FieldOption[] = [];
  @Input() taskTypeOptions: FieldOption[] = [];
  @Input() isLoadingSubtasks: boolean = false;
  /** True while the parent is refreshing the task header via `getTaskByIdFromApi`
   *  in the background. Drives a subtle "Refreshing..." indicator in the sidebar
   *  header — NOT a blocking overlay (the cached data is already visible). */
  @Input() isRefreshing: boolean = false;

  // ---- Outputs ----
  @Output() closed = new EventEmitter<void>();
  @Output() subtaskCreated = new EventEmitter<any>();
  @Output() subtaskUpdated = new EventEmitter<any>();
  /** Emitted after a successful drag-drop status change so the parent can refresh
   *  the kanban-card progress bar. The parent must NOT re-fetch subtasks. */
  @Output() subtaskStatusChanged = new EventEmitter<SubtaskModel[]>();
  @Output() subtaskClicked = new EventEmitter<SubtaskModel>();
  @Output() createSubtask = new EventEmitter<TaskModel>();
  /** Kept for backwards compat — no longer emitted from this component. */
  @Output() subtaskDropConfirmNeeded = new EventEmitter<SubtaskDropConfirmPayload>();

  // ---- Modal state ----
  showSubtaskDialog = false;
  editingSubtask: SubtaskModel | null = null;
  isSavingSubtask = false;
  /** When true, the status select inside the modal is locked to a specific value
   *  (e.g. when the user clicked the "+" button inside a particular column). */
  lockStatusInModal = false;

  subtaskForm: FormGroup;

  // ---- Subtask Assignee Search (user dropdown) ----
  subtaskAssigneeInput = '';
  subtaskAssigneeInputError: string | null = null;
  subtaskAssigneeSearchResults: any[] = [];
  isSearchingSubtaskUsers = false;
  showSubtaskAssigneeResults = false;
  subtaskAssigneeDisplayMap: Record<string, string> = {};
  private subtaskAssigneeSearchTimer: any = null;
  private subtaskAssigneeUsersCache: any[] | null = null;

  // ---- Remind Time (mat-select dropdown) ----
  readonly subtaskRemindTimeOptions: readonly { value: string; label: string }[] = this.buildSubtaskRemindTimeOptions();
  disabledSubtaskRemindTimes: Set<string> = new Set<string>();
  trackSubtaskRemindOpt = (_: number, opt: { value: string }): string => opt.value;

  // ---- Subtask Updates tab (comments API) ----
  subtaskActiveTab: 'details' | 'updates' | 'attachments' = 'details';
  subtaskUpdateForm: FormGroup;

  subtaskComments: any[] = [];
  isLoadingSubtaskComments = false;
  isPostingSubtaskComment = false;
  editingSubtaskCommentId: number | null = null;
  editingSubtaskCommentText = '';
  isSavingSubtaskEdit = false;
  isDeletingSubtaskCommentId: number | null = null;
  /** Lazy-load cache key — same pattern as TaskDetailContentComponent. */
  private subtaskCommentsLoadedForId: number | null = null;

  // ---- Subtask Attachments tab (API-backed) ----
  subtaskAttachments: any[] = [];
  isLoadingSubtaskAttachments = false;
  isUploadingSubtaskFile = false;
  subtaskUploadProgress = 0;
  isDeletingSubtaskAttachmentId: number | null = null;
  isSubtaskDragOver = false;
  /** Same lazy-load cache pattern as subtaskCommentsLoadedForId. */
  private subtaskAttachmentsLoadedForId: number | null = null;
  private readonly maxSubtaskFileSize = 50 * 1024 * 1024;

  private destroy$ = new Subject<void>();
  private isSubtaskInitialPatch = true;

  // ---- Header label maps ----
  private statusLabels: Record<string, string> = {
    planned: 'Open',
    'in-progress': 'In-Progress',
    done: 'Completed',
    cancelled: 'Cancelled',
  };

  private priorityLabels: Record<string, string> = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
  };

  constructor(
    private fb: FormBuilder,
    private taskService: TaskMgmtService,
    private sessionService: SessionService,
    private dialogService: DialogService
  ) {
    this.subtaskForm = this.fb.group({
      name:           ['', Validators.required],
      taskType:       [null],
      assignees:      [[], [SubtaskDrilldownPanelComponent.minArrayLength(1)]],
      startDate:      [null],
      endDate:        [null],
      remindMe:       [false],
      remindDate:     [null],
      remindTime:     [null],
      status:         [null, Validators.required],
      priorityLevel:  [''],
      // `tagType` was removed from the subtask dialog (it lives only in the
      // main task dialog now). The `tags` control is kept so the API payload
      // can still send `tag_ids: []`.
      tags:           [[]],
      description:    [''],
    });

    // Separate form for the Task Updates tab composer (comments API).
    this.subtaskUpdateForm = this.fb.group({
      message: ['', Validators.required],
    });

    // When remindMe is toggled, auto-populate or clear the date/time fields.
    this.subtaskForm.get('remindMe')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled: boolean) => {
        this.onSubtaskRemindMeToggle(!!enabled);
      });

    // Recompute disabled time slots when remind date changes.
    this.subtaskForm.get('remindDate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => { this.recomputeDisabledSubtaskRemindTimes(); });
  }

  // ============================================================
  //  Remind Me helpers
  // ============================================================

  /** Called when user toggles Remind Me ON/OFF in the subtask dialog.
   *  Auto-populates date to today and time to now + 30 min when enabled. */
  onSubtaskRemindMeToggle(enabled: boolean): void {
    if (this.isSubtaskInitialPatch) return;
    if (!enabled) {
      this.subtaskForm.get('remindDate')?.setValue(null);
      this.subtaskForm.get('remindTime')?.setValue(null);
      return;
    }
    // Preserve existing time if one is already set.
    const existingTime: string | null = this.subtaskForm.get('remindTime')?.value;
    if (existingTime) return;

    // Compute next 30-min slot strictly after now.
    const now = new Date();
    let h = now.getHours();
    const m = now.getMinutes() < 30 ? 30 : 0;
    if (m === 0) h++;
    let rolledToTomorrow = false;
    if (h >= 24) { h -= 24; rolledToTomorrow = true; }
    const timeStr = String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');

    // Set date: today by default, tomorrow if slot wrapped past midnight.
    const existingDate: unknown = this.subtaskForm.get('remindDate')?.value;
    const needsDate = !existingDate || (rolledToTomorrow && this.isSubtaskDateToday(existingDate));
    if (needsDate) {
      const base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (rolledToTomorrow) base.setDate(base.getDate() + 1);
      this.subtaskForm.get('remindDate')?.setValue(base);
    }
    this.subtaskForm.get('remindTime')?.setValue(timeStr);
    this.recomputeDisabledSubtaskRemindTimes();
  }

  /** minDate for remind date picker — today. Recomputed on each access. */
  get remindMinDate(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  /** Format a Date or string to YYYY-MM-DD for the API payload. */
  private formatRemindDateLocal(value: any): string | null {
    if (!value) return null;
    if (typeof value === 'string') return value.substring(0, 10);
    if (value instanceof Date) {
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, '0');
      const d = String(value.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    return null;
  }

  ngOnDestroy(): void {
    if (this.subtaskAssigneeSearchTimer) {
      clearTimeout(this.subtaskAssigneeSearchTimer);
      this.subtaskAssigneeSearchTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================================
  //  Header / metadata helpers
  // ============================================================

  getStatusLabel(status: string | null | undefined): string {
    // Prefer the raw API name when present (after the new mapper).
    const raw = (this.task as any)?._raw?.task_status_name;
    if (raw) return raw;
    if (!status) return '';
    if (this.statusLabels[status]) return this.statusLabels[status];
    // status may be a numeric id — fall back to the service lookup.
    return this.taskService.getStatusNameById(Number(status)) || String(status);
  }

  getPriorityLabel(priority: string | null | undefined): string {
    const raw = (this.task as any)?._raw?.task_priority_name;
    if (raw) return raw;
    if (!priority) return '';
    if (this.priorityLabels[priority]) return this.priorityLabels[priority];
    return this.taskService.getPriorityNameById(Number(priority)) || String(priority);
  }

  /** Map the parent task's status name to a kanban-column key for the pill class. */
  get parentStatusKey(): string {
    const name = (this.getStatusLabel(this.task?.status) || '').toLowerCase().trim();
    if (name === 'open') return 'planned';
    if (name === 'in progress' || name === 'inprogress' || name === 'in-progress') return 'in-progress';
    if (name === 'done' || name === 'closed' || name === 'complete' || name === 'completed') return 'done';
    if (name === 'cancelled' || name === 'canceled') return 'cancelled';
    return '';
  }

  /** Map the parent task's priority name to 'low' / 'medium' / 'high' for the pill class. */
  get parentPriorityKey(): string {
    const name = (this.getPriorityLabel(this.task?.priorityLevel) || '').toLowerCase().trim();
    if (name === 'low') return 'low';
    if (name === 'medium') return 'medium';
    if (name === 'high') return 'high';
    return '';
  }

  // ============================================================
  //  Subtask form dropdown helpers (resolve from statusOptions/
  //  priorityOptions arrays — NOT from the parent task model).
  // ============================================================

  statusKeyFor(value: any): string {
    const label = this.getSubtaskStatusLabel(value).toLowerCase().trim();
    if (label === 'open') return 'planned';
    if (label === 'in progress' || label === 'inprogress') return 'in-progress';
    if (label === 'done' || label === 'closed' || label === 'completed') return 'done';
    if (label === 'cancelled' || label === 'canceled') return 'cancelled';
    return '';
  }

  priorityKeyFor(value: any): string {
    const label = this.getSubtaskPriorityLabel(value).toLowerCase().trim();
    if (label === 'low') return 'low';
    if (label === 'medium') return 'medium';
    if (label === 'high') return 'high';
    return '';
  }

  getSubtaskStatusLabel(value: any): string {
    if (value == null || value === '') return '';
    const found = this.statusOptions.find(
      (s: any) => s.value === value || s.value === Number(value)
    );
    return found?.label ?? '';
  }

  getSubtaskPriorityLabel(value: any): string {
    if (value == null || value === '') return '';
    const found = this.priorityOptions.find(
      (p: any) => p.value === value || p.value === Number(value)
    );
    return found?.label ?? '';
  }

  optionLabelOf(opt: any): string {
    if (!opt) return '';
    return opt.label ?? opt.viewValue ?? '';
  }

  // ============================================================
  //  Parent task tag getters (read-only display in subtask header)
  // ============================================================

  get parentTags(): any[] {
    const raw: any = (this.task as any)?._raw?.tags;
    if (!Array.isArray(raw)) return [];
    return raw.filter((t: any) => t && typeof t === 'object' && t.tag_name);
  }

  get parentTagCategoryName(): string {
    const tags = this.parentTags;
    if (!tags.length) return '';
    return tags[0]?.category_name || '';
  }

  /** Format an ISO date as "DD Mmm YYYY" for the sidebar header due-date pill. */
  formatDate(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  /** A parent task is overdue when its end date is in the past AND it isn't done/cancelled. */
  isParentOverdue(): boolean {
    const endDate = this.task?.endDate;
    if (!endDate) return false;
    const statusName = (((this.task as any)?._raw?.task_status_name) || '').toLowerCase().trim();
    if (statusName === 'done' || statusName === 'closed' || statusName === 'complete'
      || statusName === 'completed' || statusName === 'cancelled' || statusName === 'canceled') {
      return false;
    }
    const end = new Date(endDate);
    if (isNaN(end.getTime())) return false;
    return end.getTime() < Date.now();
  }

  // ============================================================
  //  Sidebar progress-bar counts
  //
  //  Mirrors the working pattern used by the main kanban card
  //  (task-management-wf.component.html:125), which feeds the
  //  progress indicator with primitive [totalCount]/[doneCount]
  //  derived from taskService.getSubtaskCountsForParent. Here we
  //  compute the same numbers from the locally-rendered subtasks
  //  array via taskService.isDoneStatus (id → name → done check),
  //  so the bar updates on every CD tick after drag-drop, create,
  //  or edit without any subscription, setTimeout, or detectChanges.
  // ============================================================

  /** Total subtasks currently rendered in the sidebar's mini-kanban. */
  get subtaskTotalCount(): number {
    return this.subtasks?.length || 0;
  }

  /** Done count derived dynamically from each subtask's numeric status id via
   *  TaskMgmtService.isDoneStatus (resolves id → name → done/closed/complete). */
  get subtaskDoneCount(): number {
    if (!this.subtasks?.length) return 0;
    return this.subtasks.filter((st: any) => {
      const idRaw = st?.status;
      if (idRaw == null || idRaw === '') return false;
      const id = typeof idRaw === 'number' ? idRaw : Number(idRaw);
      if (isNaN(id)) return false;
      return this.taskService.isDoneStatus(id);
    }).length;
  }

  /** Comment count for the subtask dialog header. Uses the live array length
   *  once the API has responded (authoritative after add/delete CRUD), falling
   *  back to the model's _commentCount for the brief pre-load window. */
  get subtaskCommentCount(): number {
    if (this.subtaskCommentsLoadedForId != null) return this.subtaskComments.length;
    return this.editingSubtask?._commentCount ?? 0;
  }

  /** Attachment count — same pattern as subtaskCommentCount. */
  get subtaskAttachmentCount(): number {
    if (this.subtaskAttachmentsLoadedForId != null) return this.subtaskAttachments.length;
    return this.editingSubtask?._attachmentCount ?? 0;
  }

  // ============================================================
  //  Sidebar lifecycle
  // ============================================================

  onClose(): void {
    this.showSubtaskDialog = false;
    this.editingSubtask = null;
    this.closed.emit();
  }

  // ============================================================
  //  Mini-kanban event passthrough + drag-drop persistence
  // ============================================================

  /**
   * Subtask drag-drop handler — receives a `SubtaskMovePayload` from the
   * mini-kanban (which has already mutated the subtask's status optimistically),
   * persists the change via `updateTaskStatusApi`, and reverts on error.
   */
  onSubtaskMoved(payload: SubtaskMovePayload): void {
    const { subtask, previousStatus, newStatus } = payload;
    if (!subtask) return;

    // The mini-kanban has already mutated `subtask.status` to the new numeric
    // API id (and `_raw.task_status_name`). Read the id straight off the model.
    this.subtasks = [...this.subtasks];

    const statusId = subtask.status ? Number(subtask.status) : NaN;
    const subtaskIdNum = this.toNumericTaskId(subtask.subtask_id);

    if (isNaN(statusId) || isNaN(subtaskIdNum)) {
      // Revert silently — we cannot persist without valid ids.
      this.revertSubtaskStatus(subtask, previousStatus);
      this.dialogService.openSnackBar('Failed to move Child Case: invalid id.', 'OK');
      return;
    }

    this.taskService.updateTaskStatusApi({
      task_id: subtaskIdNum,
      task_status_id: statusId,
      updated_by: this.getCurrentUserName(),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.dialogService.openSnackBar('Child Case moved.', 'OK');
        this.subtaskStatusChanged.emit(this.subtasks);
      },
      error: () => {
        this.revertSubtaskStatus(subtask, previousStatus);
        this.dialogService.openSnackBar('Failed to move Child Case.', 'OK');
      },
    });
  }

  /** Revert a subtask's status to the previous kanban column key (best effort). */
  private revertSubtaskStatus(subtask: any, previousColumnKey: string): void {
    const aliases: Record<string, string[]> = {
      'planned': ['open'],
      'in-progress': ['in progress', 'inprogress', 'in-progress'],
      'done': ['done', 'closed', 'complete', 'completed'],
      'cancelled': ['cancelled', 'canceled'],
    };
    const candidates = aliases[previousColumnKey] || [previousColumnKey];
    for (const name of candidates) {
      const id = this.taskService.getStatusIdByName(name);
      if (id != null) {
        subtask.status = String(id);
        if (subtask._raw) {
          subtask._raw.task_status_name = this.taskService.getStatusNameById(id);
        }
        break;
      }
    }
    this.subtasks = [...this.subtasks];
  }

  /** Public entry point for external callers (e.g. TaskManagementWfComponent
   *  when the user clicks a subtask row inside an expanded master-detail
   *  grid) to open the subtask edit modal with fresh API data. When `initialTab`
   *  is omitted the dialog opens on the Details tab; pass 'updates' or
   *  'attachments' to land directly on that tab (e.g. from a badge icon click). */
  openSubtaskEditFromExternal(subtask: SubtaskModel, initialTab: 'details' | 'updates' | 'attachments' = 'details'): void {
    if (initialTab === 'details') {
      this.onSubtaskCardClicked(subtask);
      return;
    }

    // For non-default tabs, fetch fresh data then open on the requested tab
    // (mirrors onSubtaskCommentsClicked / onSubtaskAttachmentsClicked).
    const subtaskIdNum = this.toNumericTaskId(subtask?.subtask_id);
    if (isNaN(subtaskIdNum)) {
      this.openSubtaskEdit(subtask, initialTab);
      return;
    }
    this.taskService.getTaskByIdFromApi(subtaskIdNum)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const raw = response?.data ?? response;
          const fresh = raw ? this.mapApiResponseToSubtaskModel(raw, subtask) : subtask;
          this.openSubtaskEdit(fresh, initialTab);
        },
        error: () => this.openSubtaskEdit(subtask, initialTab),
      });
  }

  onSubtaskCardClicked(subtask: SubtaskModel): void {
    // Re-emit upward right away so listeners (e.g. analytics) can react
    // without waiting for the API.
    this.subtaskClicked.emit(subtask);

    const subtaskIdNum = this.toNumericTaskId(subtask?.subtask_id);
    if (isNaN(subtaskIdNum)) {
      // No id (mock fixture row) — open immediately with the stale model.
      this.openSubtaskEdit(subtask);
      return;
    }

    // Fetch the latest server-side state via api/tasks/get-by-id (same
    // endpoint serves tasks AND subtasks per the API spec) so the form
    // always shows fresh data. On failure, fall back to the in-memory
    // SubtaskModel so the user can still edit.
    this.taskService.getTaskByIdFromApi(subtaskIdNum)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const raw = response?.data ?? response;
          if (!raw) {
            this.openSubtaskEdit(subtask);
            return;
          }
          const fresh = this.mapApiResponseToSubtaskModel(raw, subtask);
          this.openSubtaskEdit(fresh);
        },
        error: () => {
          this.openSubtaskEdit(subtask);
          this.dialogService.openSnackBar(
            'Could not refresh Child Case data — showing cached version.',
            'OK'
          );
        },
      });
  }

  /** Handle comment badge click from mini-kanban card — fetch fresh data then
   *  open dialog on Updates tab. */
  onSubtaskCommentsClicked(subtask: SubtaskModel): void {
    const subtaskIdNum = this.toNumericTaskId(subtask?.subtask_id);
    if (isNaN(subtaskIdNum)) {
      this.openSubtaskEdit(subtask, 'updates');
      return;
    }
    this.taskService.getTaskByIdFromApi(subtaskIdNum)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const raw = response?.data ?? response;
          const fresh = raw ? this.mapApiResponseToSubtaskModel(raw, subtask) : subtask;
          this.openSubtaskEdit(fresh, 'updates');
        },
        error: () => this.openSubtaskEdit(subtask, 'updates'),
      });
  }

  /** Handle attachment badge click — same pattern, opens on Attachments tab. */
  onSubtaskAttachmentsClicked(subtask: SubtaskModel): void {
    const subtaskIdNum = this.toNumericTaskId(subtask?.subtask_id);
    if (isNaN(subtaskIdNum)) {
      this.openSubtaskEdit(subtask, 'attachments');
      return;
    }
    this.taskService.getTaskByIdFromApi(subtaskIdNum)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const raw = response?.data ?? response;
          const fresh = raw ? this.mapApiResponseToSubtaskModel(raw, subtask) : subtask;
          this.openSubtaskEdit(fresh, 'attachments');
        },
        error: () => this.openSubtaskEdit(subtask, 'attachments'),
      });
  }

  /** Map a raw `api/tasks/get-by-id` response into the SubtaskModel shape that
   *  openSubtaskEdit expects. Mirrors the logic in
   *  task-management-wf.component.ts:868 (mapStoreSubtaskToModel) but kept
   *  inline so this component stays self-contained — extracting it would
   *  require touching another file outside the allowed list. */
  private mapApiResponseToSubtaskModel(item: any, fallback: SubtaskModel | null = null): SubtaskModel {
    if (!item) return (fallback as SubtaskModel);

    const statusIdStr = item?.task_status_id != null ? String(item.task_status_id) : '';
    const priorityIdStr = item?.task_priority_id != null ? String(item.task_priority_id) : '';
    const typeIdStr = item?.task_type_id != null ? String(item.task_type_id) : '';

    let assignTo: string = item?.owner_id ?? '';
    if (!assignTo && Array.isArray(item?.assignees) && item.assignees.length) {
      assignTo = item.assignees[0]?.assignee_email ?? item.assignees[0]?.email ?? '';
    }
    if (!assignTo && Array.isArray(item?.assignee_emails) && item.assignee_emails.length) {
      assignTo = item.assignee_emails[0] ?? '';
    }

    let tags: number[] = [];
    if (Array.isArray(item?.tags)) {
      tags = item.tags
        .map((t: any) => (typeof t === 'number' ? t : (t?.tag_id ?? t?.id)))
        .filter((n: any) => typeof n === 'number');
    } else if (Array.isArray(item?.tag_ids)) {
      tags = item.tag_ids.filter((n: any) => typeof n === 'number');
    }

    const model: any = {
      subtask_id: item?.task_id != null
        ? String(item.task_id)
        : (fallback?.subtask_id ?? ''),
      name: (item?.task_name ?? fallback?.name ?? '').toString().trim(),
      taskType: typeIdStr,
      startDate: item?.start_date ?? null,
      endDate: item?.end_date ?? null,
      remindMe: false,
      remindDate: null,
      remindTime: null,
      priorityLevel: priorityIdStr,
      status: statusIdStr,
      assignTo,
      tagType: '',
      tags,
      description: (item?.description ?? '').toString().trim(),
      attachments: [],
      comments: [],
      _commentCount: item?.comment_count ?? 0,
      _attachmentCount: item?.attachment_count ?? 0,
      _raw: item,
    };
    return model as SubtaskModel;
  }

  // ============================================================
  //  Subtask create / edit modal
  // ============================================================

  /** "+ Add subtask" button in the sidebar header — opens an empty form with
   *  status ALWAYS preset to Open (resolved dynamically from the loaded status
   *  list, never hardcoded). The control is editable so the user can change it. */
  openSubtaskCreate(): void {
    // Reset the tab + comments state so the dialog starts fresh every time.
    this.resetSubtaskUpdatesState();
    // Subtasks always start as Open. Resolving via the service ensures the
    // form control holds the actual API id that the mat-option exposes.
    const openStatusId = this.taskService.getDefaultOpenStatusId();
    // Default startDate and endDate to today for new subtasks so the user
    // doesn't have to pick dates manually for the common case.
    const todayDate = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
    this.isSubtaskInitialPatch = true;
    this.subtaskForm.reset({
      name: '',
      taskType: null,
      assignees: [],
      startDate: todayDate,
      endDate: todayDate,
      remindMe: false,
      remindDate: null,
      remindTime: null,
      status: openStatusId,
      priorityLevel: '',
      tags: [],
      description: '',
    });
    setTimeout(() => { this.isSubtaskInitialPatch = false; }, 0);
    this.subtaskAssigneeInput = '';
    this.subtaskAssigneeInputError = null;
    this.subtaskForm.get('status')?.enable();
    this.editingSubtask = null;
    this.lockStatusInModal = false;
    this.showSubtaskDialog = true;
  }

  /** Per-column "+" handler emitted by the mini-kanban. Per spec: subtasks
   *  ALWAYS start as Open regardless of which column the "+" was clicked in.
   *  Delegates to `openSubtaskCreate()`. The `_statusKey` argument is ignored. */
  openSubtaskCreateInColumn(_statusKey: string): void {
    this.openSubtaskCreate();
  }

  /** Open the modal in edit mode pre-populated with the supplied subtask.
   *  `initialTab` defaults to 'details'; pass 'updates' or 'attachments'
   *  to land directly on that tab (e.g. from a badge icon click). */
  openSubtaskEdit(subtask: SubtaskModel, initialTab: 'details' | 'updates' | 'attachments' = 'details'): void {
    // Reset the tab + comments state so the dialog starts fresh every time.
    this.resetSubtaskUpdatesState();
    this.editingSubtask = subtask;
    // status / priorityLevel / taskType on the model are numeric API ids stored
    // as strings — coerce to number so they match mat-option [value] (which is
    // a number from the FieldOption list).
    const toNumOrNull = (v: any): any => {
      if (v == null || v === '') return null;
      const n = typeof v === 'number' ? v : Number(v);
      return isNaN(n) ? null : n;
    };
    const hasExistingRemind = !!(subtask?.remindMe || (subtask as any)?.remind_me);
    const existingRemindDate = subtask?.remindDate || (subtask as any)?.remind_date || null;
    const existingRemindTime = subtask?.remindTime || (subtask as any)?.remind_time || null;

    this.isSubtaskInitialPatch = true;
    this.subtaskForm.reset({
      name: (subtask.name || '').toString().trim(),
      taskType: toNumOrNull(subtask.taskType),
      assignees: [],
      startDate: subtask.startDate || null,
      endDate: subtask.endDate || null,
      remindMe: hasExistingRemind,
      remindDate: existingRemindDate ? new Date(existingRemindDate) : null,
      remindTime: existingRemindTime || null,
      status: toNumOrNull(subtask.status),
      priorityLevel: toNumOrNull(subtask.priorityLevel),
      tags: Array.isArray(subtask.tags) ? subtask.tags : [],
      description: (subtask.description || '').toString().trim(),
    });

    // Restore assignees from the raw API data or legacy single assignTo field.
    const rawAssignees = (subtask as any)?._raw?.assignees
      || (subtask as any)?._raw?.assignee_emails || [];
    const stEmails: string[] = Array.isArray(rawAssignees)
      ? rawAssignees.map((a: any) => typeof a === 'string' ? a : (a?.assignee_email || '')).filter(Boolean)
      : [];
    if (stEmails.length === 0 && subtask.assignTo) {
      stEmails.push(subtask.assignTo);
    }
    this.subtaskForm.get('assignees')?.setValue(stEmails);
    this.subtaskAssigneeInput = '';
    this.subtaskAssigneeInputError = null;

    // Populate display name map for assignee chips (edit mode).
    this.subtaskAssigneeDisplayMap = {};
    if (Array.isArray(rawAssignees)) {
      rawAssignees.forEach((a: any) => {
        if (typeof a === 'object' && a?.assignee_email) {
          this.subtaskAssigneeDisplayMap[a.assignee_email] = a?.usr_display_nm || a?.display_name || a.assignee_email;
        }
      });
    }

    // Compute min-time constraint for the native time input.
    this.recomputeDisabledSubtaskRemindTimes();

    this.subtaskForm.get('status')?.enable();
    this.lockStatusInModal = false;
    this.showSubtaskDialog = true;
    // Eager-load comments and attachments so the tab header counts reflect
    // server state immediately on dialog open — mirrors the parent dialog
    // pattern at task-detail-content.component.ts:443–444. The lazy-load
    // cache keys prevent duplicate fetches when the user clicks the tab.
    this.loadSubtaskCommentsIfNeeded();
    this.loadSubtaskAttachmentsIfNeeded();
    setTimeout(() => { this.isSubtaskInitialPatch = false; }, 0);
    if (initialTab !== 'details') {
      setTimeout(() => { this.onSubtaskTabChange(initialTab); }, 0);
    }
  }

  closeSubtaskDialog(): void {
    this.showSubtaskDialog = false;
    this.editingSubtask = null;
    this.lockStatusInModal = false;
    this.subtaskForm.get('status')?.enable();
  }

  // ============================================================
  //  Subtask Assignee Chip Input
  // ============================================================

  onSubtaskAssigneeInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addSubtaskAssigneeFromInput();
    }
  }

  addSubtaskAssigneeFromInput(): void {
    const email = (this.subtaskAssigneeInput || '').trim().toLowerCase();
    this.subtaskAssigneeInputError = null;
    if (!email) return;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      this.subtaskAssigneeInputError = 'Enter a valid email address';
      return;
    }
    const current: string[] = this.subtaskForm.get('assignees')?.value || [];
    if (current.includes(email)) {
      this.subtaskAssigneeInputError = 'Email already added';
      return;
    }
    this.subtaskForm.get('assignees')?.setValue([...current, email]);
    this.subtaskForm.get('assignees')?.markAsTouched();
    this.subtaskAssigneeInput = '';
  }

  removeSubtaskAssignee(index: number): void {
    const current: string[] = this.subtaskForm.get('assignees')?.value || [];
    this.subtaskForm.get('assignees')?.setValue(current.filter((_: any, i: number) => i !== index));
  }

  getSubtaskAssignees(): string[] {
    return this.subtaskForm.get('assignees')?.value || [];
  }

  /** Click on the dimmed backdrop closes the dialog (clicks inside the modal stop propagation). */
  onOverlayClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target?.classList?.contains('st-overlay')) {
      this.closeSubtaskDialog();
    }
  }

  saveSubtask(): void {
    if (this.subtaskForm.invalid || this.isSavingSubtask) return;
    if (!this.task) return;

    const raw = this.subtaskForm.getRawValue();
    const currentUser = this.getCurrentUserName();
    const parentTaskId = this.toNumericTaskId(this.task.task_id);

    const remindDateStr = raw.remindMe ? this.formatRemindDateLocal(raw.remindDate) : null;
    const remindTimeStr = raw.remindMe ? (raw.remindTime || null) : null;

    const payload: any = {
      task_name: raw.name,
      parent_task_id: isNaN(parentTaskId) ? null : parentTaskId,
      task_status_id: raw.status != null ? Number(raw.status) : null,
      task_priority_id: raw.priorityLevel !== '' && raw.priorityLevel != null ? Number(raw.priorityLevel) : null,
      task_type_id: raw.taskType != null ? Number(raw.taskType) : null,
      description: raw.description || null,
      start_date: raw.startDate || null,
      end_date: raw.endDate || null,
      remind_me: !!raw.remindMe,
      remind_date: remindDateStr,
      remind_time: remindTimeStr,
      brand_key: 1,
      assignee_emails: raw.assignees || [],
      tag_ids: [],
      created_by: currentUser,
      updated_by: currentUser,
    };

    this.isSavingSubtask = true;

    if (this.editingSubtask) {
      // Edit mode — call updateTaskApi with task_id of the subtask.
      const subtaskId = this.toNumericTaskId(this.editingSubtask.subtask_id);
      const updatePayload = { ...payload, task_id: isNaN(subtaskId) ? null : subtaskId };
      this.taskService.updateTaskApi(updatePayload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.isSavingSubtask = false;
            this.dialogService.openSnackBar('Child Case updated.', 'OK');
            this.subtaskUpdated.emit(res);
            this.closeSubtaskDialog();
          },
          error: () => {
            this.isSavingSubtask = false;
            this.dialogService.openSnackBar('Failed to update Child Case.', 'OK');
          },
        });
    } else {
      // Create mode — call createTaskApi.
      this.taskService.createTaskApi(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.isSavingSubtask = false;
            this.dialogService.openSnackBar('Child Case created.', 'OK');
            this.subtaskCreated.emit(res);
            this.closeSubtaskDialog();
          },
          error: () => {
            this.isSavingSubtask = false;
            this.dialogService.openSnackBar('Failed to create Child Case.', 'OK');
          },
        });
    }
  }

  // ============================================================
  //  Lookup helpers
  // ============================================================

  private getCurrentUserName(): string | null {
    const fromSession = this.sessionService?.getUsername?.();
    if (fromSession) return fromSession;
    try {
      const raw = sessionStorage.getItem('userInformation');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.usr_display_nm || null;
    } catch {
      return null;
    }
  }

  private toNumericTaskId(id: string | number | null | undefined): number {
    if (id == null) return NaN;
    if (typeof id === 'number') return id;
    const n = parseInt(String(id).replace(/^[A-Z]+/i, '').replace(/[^\d]/g, ''), 10);
    return isNaN(n) ? NaN : n;
  }

  // ============================================================
  //  Subtask Task-Updates tab (comments API)
  //
  //  Loaded lazily when the user clicks the Updates tab — same
  //  backend endpoints as the main task dialog. Subtasks are
  //  tasks with a parent_task_id, so the API doesn't distinguish.
  // ============================================================

  /** Reset all comment/attachment/tab state so the dialog starts fresh on every open. */
  private resetSubtaskUpdatesState(): void {
    this.subtaskActiveTab = 'details';
    this.subtaskComments = [];
    this.subtaskCommentsLoadedForId = null;
    this.subtaskUpdateForm.reset({ message: '' });
    this.editingSubtaskCommentId = null;
    this.editingSubtaskCommentText = '';
    this.isLoadingSubtaskComments = false;
    this.isPostingSubtaskComment = false;
    this.isSavingSubtaskEdit = false;
    this.isDeletingSubtaskCommentId = null;
    // Attachments tab state
    this.subtaskAttachments = [];
    this.subtaskAttachmentsLoadedForId = null;
    this.isLoadingSubtaskAttachments = false;
    this.isUploadingSubtaskFile = false;
    this.subtaskUploadProgress = 0;
    this.isDeletingSubtaskAttachmentId = null;
    this.isSubtaskDragOver = false;
  }

  /** Tab switch handler — lazy-loads comments on 'updates' and attachments
   *  on 'attachments' the first time the user clicks each tab for a given
   *  subtask. The per-tab cache keys prevent re-fetches on subsequent tab
   *  toggles within the same subtask. */
  onSubtaskTabChange(tab: 'details' | 'updates' | 'attachments'): void {
    this.subtaskActiveTab = tab;
    if (tab === 'updates') {
      this.loadSubtaskCommentsIfNeeded();
      if (this.subtaskComments.length > 0) {
        this.scrollSubtaskUpdatesToBottom(false);
      }
    } else if (tab === 'attachments') {
      this.loadSubtaskAttachmentsIfNeeded();
    }
  }

  /** Numeric id of the subtask currently being edited — used as both the
   *  `task_id` for the comments API and the lazy-load cache key. Returns
   *  null in create mode (no subtask yet). */
  private getSubtaskIdNum(): number | null {
    if (!this.editingSubtask) return null;
    const n = this.toNumericTaskId(this.editingSubtask.subtask_id);
    return isNaN(n) ? null : n;
  }

  /** Resolve the logged-in user's EMAIL synchronously. Reads the `authUser`
   *  blob that SessionService stores in sessionStorage after the first
   *  `getUserAttributes()` call (runs during login via AWS Amplify). Falls
   *  back to `userInformation.usr_display_nm` so we never send an empty string. */
  private getCurrentUserEmail(): string {
    try {
      const authUserRaw = sessionStorage.getItem('authUser');
      if (authUserRaw) {
        const parsed = JSON.parse(authUserRaw);
        const email = parsed?.attributes?.email;
        if (email) return String(email);
      }
    } catch { /* fall through */ }
    try {
      const uiRaw = sessionStorage.getItem('userInformation');
      if (uiRaw) {
        const parsed = JSON.parse(uiRaw);
        return parsed?.usr_display_nm || '';
      }
    } catch { /* fall through */ }
    return '';
  }

  private loadSubtaskCommentsIfNeeded(): void {
    const subtaskId = this.getSubtaskIdNum();
    if (subtaskId == null) return; // create mode — no subtask id yet
    if (this.subtaskCommentsLoadedForId === subtaskId) return;
    this.isLoadingSubtaskComments = true;
    this.taskService.getTaskComments(subtaskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.subtaskComments = Array.isArray(response) ? response
            : (Array.isArray(response?.items) ? response.items
            : (Array.isArray(response?.data) ? response.data : []));
          this.subtaskCommentsLoadedForId = subtaskId;
          this.isLoadingSubtaskComments = false;
          this.scrollSubtaskUpdatesToBottom(false);
        },
        error: () => {
          this.subtaskComments = [];
          this.isLoadingSubtaskComments = false;
        },
      });
  }

  /** Scroll the Updates tab content to the bottom. Uses the `.st-modal-body`
   *  wrapper as the scroll target. `smooth` on post, instant on initial load. */
  private scrollSubtaskUpdatesToBottom(smooth: boolean = true): void {
    setTimeout(() => {
      const el = this.stModalBody?.nativeElement;
      if (!el) return;
      if (smooth) {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    }, 50);
  }

  /** Auto-resize the composer textarea up to 100px. */
  onSubtaskComposerInput(event: Event): void {
    const ta = event.target as HTMLTextAreaElement;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 100) + 'px';
  }

  /** Enter posts the comment, Shift+Enter inserts a newline. */
  onSubtaskComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (this.subtaskUpdateForm.get('message')?.value?.trim() && !this.isPostingSubtaskComment) {
        this.onAddSubtaskUpdate();
      }
    }
  }

  onAddSubtaskUpdate(): void {
    if (this.subtaskUpdateForm.invalid || this.isPostingSubtaskComment) return;
    const message = (this.subtaskUpdateForm.get('message')?.value || '').toString().trim();
    if (!message) return;
    const subtaskId = this.getSubtaskIdNum();
    if (subtaskId == null) return; // can't comment before the subtask is saved

    this.isPostingSubtaskComment = true;
    const userEmail = this.getCurrentUserEmail();

    this.taskService.addTaskComment(subtaskId, message, userEmail)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (created: any) => {
          if (created && typeof created === 'object' && created.task_update_id != null) {
            this.subtaskComments = [...this.subtaskComments, created];
          } else {
            this.subtaskComments = [...this.subtaskComments, {
              task_update_id: null,
              comment_text: message,
              created_by: userEmail,
              created_time: new Date().toISOString(),
              attachments: [],
            }];
          }
          this.subtaskUpdateForm.reset({ message: '' });
          this.isPostingSubtaskComment = false;
          this.scrollSubtaskUpdatesToBottom(true);
          // Propagate count to the allSubtasks$ store so compact card badges update.
          this.propagateSubtaskCommentCount();
          this.dialogService.openSnackBar('Comment added', 'OK');
        },
        error: () => {
          this.isPostingSubtaskComment = false;
        },
      });
  }

  startEditSubtaskComment(comment: any): void {
    this.editingSubtaskCommentId = comment?.task_update_id ?? null;
    this.editingSubtaskCommentText = (comment?.comment_text || '').toString();
  }

  cancelEditSubtaskComment(): void {
    this.editingSubtaskCommentId = null;
    this.editingSubtaskCommentText = '';
  }

  saveEditSubtaskComment(comment: any): void {
    const text = (this.editingSubtaskCommentText || '').trim();
    if (!text || this.isSavingSubtaskEdit) return;
    const subtaskId = this.getSubtaskIdNum();
    if (subtaskId == null || comment?.task_update_id == null) return;

    this.isSavingSubtaskEdit = true;
    this.taskService.editTaskComment(subtaskId, comment.task_update_id, text)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const idx = this.subtaskComments.findIndex((c) => c.task_update_id === comment.task_update_id);
          if (idx > -1) {
            this.subtaskComments[idx] = { ...this.subtaskComments[idx], comment_text: text };
            this.subtaskComments = [...this.subtaskComments];
          }
          this.cancelEditSubtaskComment();
          this.isSavingSubtaskEdit = false;
          this.dialogService.openSnackBar('Comment updated', 'OK');
        },
        error: () => {
          this.isSavingSubtaskEdit = false;
        },
      });
  }

  deleteSubtaskComment(comment: any): void {
    if (this.isDeletingSubtaskCommentId != null) return;
    const subtaskId = this.getSubtaskIdNum();
    if (subtaskId == null || comment?.task_update_id == null) return;

    this.isDeletingSubtaskCommentId = comment.task_update_id;
    this.taskService.deleteTaskComment(subtaskId, comment.task_update_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.subtaskComments = this.subtaskComments.filter((c) => c.task_update_id !== comment.task_update_id);
          this.isDeletingSubtaskCommentId = null;
          this.propagateSubtaskCommentCount();
          this.dialogService.openSnackBar('Comment deleted', 'OK');
        },
        error: () => {
          this.isDeletingSubtaskCommentId = null;
        },
      });
  }

  /** Push the current subtask comment count into the allSubtasks$ store so
   *  compact card badges and table detail rows update in real time. */
  private propagateSubtaskCommentCount(): void {
    const subtaskId = this.getSubtaskIdNum();
    if (subtaskId != null) {
      this.taskService.emitAndApplySubtaskCountChange(subtaskId, this.subtaskComments.length, undefined);
    }
  }

  /** Push the current subtask attachment count into the allSubtasks$ store. */
  private propagateSubtaskAttachmentCount(): void {
    const subtaskId = this.getSubtaskIdNum();
    if (subtaskId != null) {
      this.taskService.emitAndApplySubtaskCountChange(subtaskId, undefined, this.subtaskAttachments.length);
    }
  }

  // ========== Display helpers (shared with TaskDetailContentComponent) ==========
  //
  // Duplicated verbatim so the subtask dialog is self-contained — extracting
  // these into a shared util would require a new file, which is out of scope.

  getCommentInitials(email: string | null | undefined): string {
    if (!email) return '?';
    const prefix = (email.split('@')[0]) || email;
    const parts = prefix.split(/[._\-+]/).filter((p: string) => p.length > 0);
    const first = (parts[0]?.[0] || '').toUpperCase();
    const second = (parts[1]?.[0] || parts[0]?.[1] || '').toUpperCase();
    return (first + second) || '?';
  }

  getRelativeTime(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return Math.floor(diffSec / 60) + 'm ago';
    if (diffSec < 86400) return Math.floor(diffSec / 3600) + 'h ago';
    if (diffSec < 604800) return Math.floor(diffSec / 86400) + 'd ago';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  getFullTimestamp(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB');
  }

  // ============================================================
  //  Subtask Attachments tab (API-backed)
  //
  //  Mirrors the TaskDetailContentComponent attachment logic but
  //  scoped to the subtask modal. Lazy-loaded on first click of
  //  the Attachments tab via onSubtaskTabChange('attachments').
  //  Extracting this into a shared mixin would require a new
  //  file, which is outside the allowed scope.
  // ============================================================

  private loadSubtaskAttachmentsIfNeeded(): void {
    const subtaskId = this.getSubtaskIdNum();
    if (subtaskId == null) return;
    if (this.subtaskAttachmentsLoadedForId === subtaskId) return;
    this.isLoadingSubtaskAttachments = true;
    this.taskService.getTaskAttachments(subtaskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.subtaskAttachments = Array.isArray(response) ? response
            : (Array.isArray(response?.items) ? response.items
            : (Array.isArray(response?.data) ? response.data : []));
          this.subtaskAttachmentsLoadedForId = subtaskId;
          this.isLoadingSubtaskAttachments = false;
          this.propagateSubtaskAttachmentCount();
        },
        error: () => {
          this.subtaskAttachments = [];
          this.isLoadingSubtaskAttachments = false;
        },
      });
  }

  getFileIcon(fileName: string): string {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    const iconMap: Record<string, string> = {
      pdf: 'picture_as_pdf',
      doc: 'description', docx: 'description',
      xls: 'table_chart', xlsx: 'table_chart', csv: 'table_chart',
      ppt: 'slideshow', pptx: 'slideshow',
      txt: 'article',
      jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image', svg: 'image',
      zip: 'folder_zip', rar: 'folder_zip', '7z': 'folder_zip',
      mp4: 'movie', mov: 'movie', avi: 'movie',
      mp3: 'audiotrack', wav: 'audiotrack',
    };
    return iconMap[ext] || 'attach_file';
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  // --- Drag & drop handlers ---

  onSubtaskDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isSubtaskDragOver = true;
  }

  onSubtaskDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isSubtaskDragOver = false;
  }

  onSubtaskFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isSubtaskDragOver = false;
    const files = event.dataTransfer?.files;
    if (files?.length) this.uploadSubtaskFiles(Array.from(files));
  }

  onSubtaskAttachmentInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.uploadSubtaskFiles(Array.from(input.files));
      input.value = '';
    }
  }

  uploadSubtaskFiles(files: File[]): void {
    const subtaskId = this.getSubtaskIdNum();
    if (subtaskId == null) return;
    const userEmail = this.getCurrentUserEmail();

    const validFiles: File[] = [];
    let anyRejected = false;
    for (const f of files) {
      if (f.size > this.maxSubtaskFileSize) {
        anyRejected = true;
      } else {
        validFiles.push(f);
      }
    }
    if (anyRejected) {
      this.dialogService.openSnackBar(
        'One or more files exceeded the 50 MB limit and were skipped.',
        'OK'
      );
    }
    if (validFiles.length === 0) return;

    this.uploadNextSubtaskFile(validFiles, 0, subtaskId, userEmail);
  }

  private uploadNextSubtaskFile(files: File[], index: number, subtaskId: number, userEmail: string): void {
    if (index >= files.length) {
      this.isUploadingSubtaskFile = false;
      this.subtaskUploadProgress = 0;
      const uploadedCount = files.length;
      this.dialogService.openSnackBar(
        uploadedCount === 1 ? 'Attachment uploaded' : uploadedCount + ' attachments uploaded', 'OK'
      );
      this.subtaskAttachmentsLoadedForId = null;
      this.loadSubtaskAttachmentsIfNeeded();
      return;
    }

    this.isUploadingSubtaskFile = true;
    this.subtaskUploadProgress = Math.round((index / files.length) * 100);

    this.taskService.uploadTaskAttachment(subtaskId, files[index], userEmail)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.subtaskUploadProgress = Math.round(((index + 1) / files.length) * 100);
          this.uploadNextSubtaskFile(files, index + 1, subtaskId, userEmail);
        },
        error: () => {
          this.uploadNextSubtaskFile(files, index + 1, subtaskId, userEmail);
        },
      });
  }

  downloadSubtaskAttachment(att: any): void {
    const subtaskId = this.getSubtaskIdNum();
    if (subtaskId == null || att?.attachment_id == null) return;

    this.taskService.getAttachmentPresignedUrl(subtaskId, att.attachment_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const url = response?.url ?? response?.presigned_url ?? response;
          if (typeof url === 'string' && url) {
            window.open(url, '_blank', 'noopener');
          }
        },
        error: () => { /* swallowed — user can retry */ },
      });
  }

  deleteSubtaskAttachment(att: any): void {
    if (this.isDeletingSubtaskAttachmentId != null) return;
    const subtaskId = this.getSubtaskIdNum();
    if (subtaskId == null || att?.attachment_id == null) return;

    this.isDeletingSubtaskAttachmentId = att.attachment_id;
    this.taskService.deleteTaskAttachment(subtaskId, att.attachment_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.subtaskAttachments = this.subtaskAttachments.filter(
            (a) => a.attachment_id !== att.attachment_id
          );
          this.isDeletingSubtaskAttachmentId = null;
          this.propagateSubtaskAttachmentCount();
          this.dialogService.openSnackBar('Attachment deleted', 'OK');
        },
        error: () => {
          this.isDeletingSubtaskAttachmentId = null;
        },
      });
  }

  // ============================================================
  //  Remind Time (mat-select dropdown) helpers
  // ============================================================

  private buildSubtaskRemindTimeOptions(): readonly { value: string; label: string }[] {
    const list: { value: string; label: string }[] = [];
    for (let h = 0; h < 24; h++) {
      for (const m of [0, 30]) {
        const v = String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
        list.push({ value: v, label: v });
      }
    }
    return list;
  }

  /** Recompute which 30-min slots are in the past (today only). */
  recomputeDisabledSubtaskRemindTimes(): void {
    const dateVal: unknown = this.subtaskForm.get('remindDate')?.value;
    if (!this.isSubtaskDateToday(dateVal)) {
      if (this.disabledSubtaskRemindTimes.size > 0) this.disabledSubtaskRemindTimes = new Set<string>();
      return;
    }
    const now = new Date();
    const curH = now.getHours();
    const curM = now.getMinutes();
    const next = new Set<string>();
    for (const opt of this.subtaskRemindTimeOptions) {
      const h = Number(opt.value.substring(0, 2));
      const m = Number(opt.value.substring(3, 5));
      if (h < curH || (h === curH && m <= curM)) next.add(opt.value);
    }
    this.disabledSubtaskRemindTimes = next;
  }

  private isSubtaskDateToday(value: unknown): boolean {
    if (!value) return false;
    const d = value instanceof Date ? value : new Date(value as string);
    if (isNaN(d.getTime())) return false;
    const now = new Date();
    return d.getFullYear() === now.getFullYear()
        && d.getMonth() === now.getMonth()
        && d.getDate() === now.getDate();
  }

  /** True when the selected remind time is in the disabled set. */
  get isSubtaskRemindTimeInPast(): boolean {
    const timeStr: string | null = this.subtaskForm.get('remindTime')?.value;
    if (!timeStr) return false;
    return this.disabledSubtaskRemindTimes.has(timeStr);
  }

  // ============================================================
  //  Assignee Search (user dropdown) helpers
  // ============================================================

  onSubtaskAssigneeSearchFocus(): void {
    this.showSubtaskAssigneeResults = true;
    if (!this.subtaskAssigneeUsersCache) {
      this.loadSubtaskAllUsers('');
    }
  }

  onSubtaskAssigneeSearchInput(event: Event): void {
    const value = ((event.target as HTMLInputElement).value || '').trim();
    this.showSubtaskAssigneeResults = true;
    if (this.subtaskAssigneeSearchTimer) clearTimeout(this.subtaskAssigneeSearchTimer);
    this.subtaskAssigneeSearchTimer = setTimeout(() => this.filterSubtaskUsers(value), 200);
  }

  onSubtaskAssigneeSearchBlur(): void {
    setTimeout(() => { this.showSubtaskAssigneeResults = false; }, 200);
  }

  private loadSubtaskAllUsers(keyword: string): void {
    this.isSearchingSubtaskUsers = true;
    this.taskService.getAllUserForImpersonate(keyword)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.subtaskAssigneeUsersCache = Array.isArray(response) ? response : (response?.data || []);
          this.filterSubtaskUsers(keyword);
          this.isSearchingSubtaskUsers = false;
        },
        error: () => {
          this.subtaskAssigneeUsersCache = [];
          this.subtaskAssigneeSearchResults = [];
          this.isSearchingSubtaskUsers = false;
        },
      });
  }

  private filterSubtaskUsers(keyword: string): void {
    if (!this.subtaskAssigneeUsersCache) return;
    if (!keyword) {
      this.subtaskAssigneeSearchResults = this.subtaskAssigneeUsersCache.slice(0, 50);
      return;
    }
    const lower = keyword.toLowerCase();
    this.subtaskAssigneeSearchResults = this.subtaskAssigneeUsersCache
      .filter((u: any) =>
        (u.usr_display_nm || '').toLowerCase().includes(lower) ||
        (u.user_id || '').toLowerCase().includes(lower)
      )
      .slice(0, 50);
  }

  isSubtaskUserAlreadySelected(user: any): boolean {
    const current: string[] = this.subtaskForm.get('assignees')?.value || [];
    return current.includes(user?.user_id);
  }

  onSubtaskUserResultSelect(user: any): void {
    const email = user?.user_id;
    if (!email) return;
    const current: string[] = this.subtaskForm.get('assignees')?.value || [];
    if (current.includes(email)) return;
    this.subtaskForm.get('assignees')?.setValue([...current, email]);
    this.subtaskForm.get('assignees')?.markAsTouched();
    this.subtaskAssigneeDisplayMap[email] = user?.usr_display_nm || email;
    this.subtaskAssigneeSearchResults = [...this.subtaskAssigneeSearchResults];
  }

  getSubtaskAssigneeDisplayName(email: string): string {
    return this.subtaskAssigneeDisplayMap?.[email] || email;
  }
}
